import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtPrice, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { Button } from "@/components/ui/button";
import MapView from "@/components/MapView";
import { Bike, MapPin, Phone, Package, DollarSign, LogOut, CheckCircle2, Loader2, Navigation, ListChecks, Radio } from "lucide-react";
import { toast } from "sonner";
import { playNotificationSound } from "@/lib/sound";
import ColorThemeSwitcher from "@/components/ColorThemeSwitcher";
import MobileTabBar from "@/components/MobileTabBar";

const KAFR_NABL = { lat: 35.6146, lng: 36.5637 };

export default function DriverDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("available");
  const [available, setAvailable] = useState([]);
  const [mine, setMine] = useState([]);
  const [stats, setStats] = useState({ deliveries: 0, earnings: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const seenAvail = useRef(null);
  usePushNotifications();

  const load = async () => {
    try {
      const [a, m, s] = await Promise.all([api.get("/orders/available"), api.get("/orders/driver"), api.get("/driver/stats")]);
      if (seenAvail.current !== null) {
        const fresh = a.data.filter((o) => !seenAvail.current.has(o.id));
        if (fresh.length > 0) { toast.info(`طلب جديد متاح للتوصيل! (${fresh.length})`, { duration: 6000 }); playNotificationSound(); }
      }
      seenAvail.current = new Set(a.data.map((o) => o.id));
      setAvailable(a.data); setMine(m.data); setStats(s.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const activeIds = mine.filter((o) => o.status === "out_for_delivery").map((o) => o.id);
    if (activeIds.length === 0) return;
    let cancelled = false;
    const send = (lat, lng) => activeIds.forEach((id) => api.put(`/orders/${id}/location`, { lat, lng }).catch(() => {}));
    const jitter = () => 0.008 * (Math.random() - 0.5);
    const tick = () => {
      if (cancelled) return;
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => send(pos.coords.latitude, pos.coords.longitude),
          () => send(KAFR_NABL.lat + jitter(), KAFR_NABL.lng + jitter()),
          { enableHighAccuracy: true, timeout: 5000, maximumAge: 3000 }
        );
      } else {
        send(KAFR_NABL.lat + jitter(), KAFR_NABL.lng + jitter());
      }
    };
    tick();
    const t = setInterval(tick, 6000);
    return () => { cancelled = true; clearInterval(t); };
  }, [mine]);

  const accept = async (id) => {
    try { await api.post(`/orders/${id}/accept-delivery`); toast.success("قبلت التوصيل!"); setTab("mine"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deliver = async (id) => {
    try { await api.put(`/orders/${id}/status`, { status: "delivered" }); toast.success("تم التوصيل بنجاح!"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const active = mine.filter((o) => o.status === "out_for_delivery");
  const done = mine.filter((o) => o.status === "delivered");

  const navItems = [
    { id: "available", label: "طلبات متاحة", icon: ListChecks },
    { id: "mine", label: "توصيلاتي", icon: Bike }
  ];

  return (
    <div className="min-h-screen bg-background pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-8" dir="rtl" data-testid="driver-dashboard">
      <header className="brand-panel text-white shadow-brand" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-2xl mx-auto px-4 py-3 md:px-5 md:py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-white grid place-items-center text-primary shadow-xl"><Bike className="w-5 h-5" /></div>
              <div><div className="font-display font-bold text-sm md:text-base">أهلاً {user?.name?.split(" ")[0]}</div><div className="text-[10px] md:text-xs text-primary-foreground/70">لوحة السائق</div></div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ColorThemeSwitcher variant="mobile" />
            <button onClick={() => { logout(); navigate("/"); }} aria-label="تسجيل الخروج" className="grid h-10 w-10 place-items-center rounded-full text-white/80 active:bg-white/10">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-4 pb-3 md:px-5 md:pb-6 grid grid-cols-3 gap-2 md:gap-3">
          {[["توصيلات", stats.deliveries, Package], ["نشطة", stats.active, Navigation], ["الأرباح", fmtPrice(stats.earnings), DollarSign]].map(([l, v, Icon]) => (
            <div key={l} className="bg-white/10 rounded-xl md:rounded-2xl p-2 md:p-3 text-center">
              <Icon className="hidden md:block w-5 h-5 mx-auto text-rating" />
              <div className="font-display font-bold text-xs md:mt-1 md:text-sm">{v}</div>
              <div className="text-[11px] text-primary-foreground/70">{l}</div>
            </div>
          ))}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 md:px-5">
        <div className="hidden md:flex gap-2 my-5">
          {navItems.map(item => (
            <button key={item.id} onClick={() => setTab(item.id)} className={`flex-1 h-11 rounded-full font-semibold text-sm flex items-center justify-center gap-2 transition-colors ${tab === item.id ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground"}`}>
              <item.icon className="w-4 h-4" /> {item.label} {item.id === "available" ? `(${available.length})` : ""}
            </button>
          ))}
        </div>

        {tab === "available" && (
          available.length === 0 ? <Empty text="لا توجد طلبات متاحة حالياً" /> : (
            <div className="space-y-3">
              {available.map((o) => <OrderCard key={o.id} o={o} action={<Button data-testid={`accept-delivery-${o.id}`} onClick={() => accept(o.id)} className="rounded-full h-11 w-full font-bold">قبول التوصيل</Button>} />)}
            </div>
          )
        )}

        {tab === "mine" && (
          <div className="space-y-6">
            <div>
              <h3 className="font-display font-bold mb-3">قيد التوصيل</h3>
              {active.length === 0 ? <Empty text="لا توجد توصيلات نشطة" /> : (
                <div className="space-y-3">
                  {active.map((o) => (
                    <OrderCard key={o.id} o={o} sharing action={
                      <Button data-testid={`deliver-order-${o.id}`} onClick={() => deliver(o.id)} className="rounded-full h-11 w-full font-bold bg-primary text-primary-foreground hover:brightness-105">
                        <CheckCircle2 className="w-5 h-5 ms-1" /> تم التسليم
                      </Button>
                    } />
                  ))}
                </div>
              )}
            </div>
            {done.length > 0 && (
              <div>
                <h3 className="font-display font-bold mb-3">مكتملة</h3>
                <div className="space-y-3">{done.map((o) => <OrderCard key={o.id} o={o} muted />)}</div>
              </div>
            )}
          </div>
        )}
      </div>
      <MobileTabBar
        items={navItems.map((item) => ({ ...item, label: item.id === "available" ? "المتاحة" : "توصيلاتي", badge: item.id === "available" ? available.length : 0 }))}
        activeId={tab}
        onSelect={(id) => setTab(id)}
        label="قائمة السائق"
      />
    </div>
  );
}

function OrderCard({ o, action, muted, sharing }) {
  return (
    <div data-testid={`driver-order-${o.id}`} className={`bg-card rounded-3xl p-5 border border-border ${muted ? "opacity-70" : "shadow-card"}`}>
      <div className="flex items-center justify-between">
        <span className="font-display font-bold">{o.shop_name}</span>
        <span className="font-bold text-primary bg-primary/15 px-3 py-1 rounded-full text-sm">{fmtPrice(o.delivery_fee)}</span>
      </div>
      <div className="mt-3 space-y-2 text-sm">
        <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-primary shrink-0" /><span>{o.delivery_address}</span></div>
        <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-primary" /><a href={`tel:${o.phone}`} dir="ltr" className="font-semibold">{o.phone}</a></div>
        <div className="flex items-center gap-2 text-muted-foreground"><Package className="w-4 h-4" /> {o.items.reduce((s, i) => s + i.qty, 0)} قطعة • الإجمالي {fmtPrice(o.total)}</div>
      </div>
      {o.note && <p className="text-xs bg-muted rounded-lg p-2 mt-2">ملاحظة: {o.note}</p>}
      {o.dest_location?.lat != null && o.dest_location?.lng != null && (
        <div className="mt-4 rounded-[22px] bg-muted/55 p-2.5" data-testid={`customer-location-${o.id}`}>
          <div className="mb-2 flex items-center gap-2 px-1 text-xs font-extrabold">
            <MapPin className="h-4 w-4 text-primary" />
            موقع العميل على الخريطة
          </div>
          <MapView destination={o.dest_location} height={210} testid={`driver-map-${o.id}`} />
        </div>
      )}
      {sharing && (
        <div className="flex items-center gap-1.5 text-xs text-success mt-3" data-testid={`sharing-${o.id}`}>
          <Radio className="w-3.5 h-3.5 animate-pulse" /> جارٍ مشاركة موقعك مع المشتري
        </div>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-center py-14 text-muted-foreground"><Bike className="w-12 h-12 mx-auto opacity-30" /><p className="mt-3 text-sm">{text}</p></div>;
}
